import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {Button, Dialog, Icon} from '@startlibs/components'
import {FormValue, TextInput} from '@startlibs/form'
import {ConfirmDialog, useDialogWithToggle} from '../../hooks/useConfirmDialog'
import {setNotification} from '../../components/Notifications'
import {buildValidation, maxLength, minLength, required} from '../../utils/validation'
import {deleteCase} from '../../request/utils'

const MIN_REASON_LENGTH = 50
const MAX_REASON_LENGTH = 1000
const DELETE_CONFIRMATION = 'delete'

const preValidation = buildValidation({
  "reason": [required, minLength(MIN_REASON_LENGTH), maxLength(MAX_REASON_LENGTH)],
  "deleteConfirmation": [(value) => value !== DELETE_CONFIRMATION && 'Please type delete to confirm.']
})

const DeleteDialogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  background: #bd2619;
  color: #fff;
  margin: -2rem -4rem 1.5rem -2rem;
  padding: 1rem 2.75rem 1.25rem 1.5rem;
  border-radius: 8px 8px 0 0;
  position: relative;
`

const DeleteDialogTitleText = styled.div`
  flex-grow: 1;

  strong {
    display: block;
    font-size: 21px;
    line-height: 1.25;
  }

  span {
    display: block;
    font-size: 13px;
    font-weight: normal;
    line-height: 1;
  }
`

// Sized/colored proportionally to how Dialog's own corner "icon" prop scales
// Icon up for a decorative illustration (font-size driven, see DialogIcon in
// @startlibs/components), adapted here for white-on-red inline placement.

// Dialog's own default close icon (rendered with a stable "JS-dialog-close"
// className) reads as dark-on-white by default; since Dialog itself is a
// styled-component that forwards className through, extend it the normal
// styled-components way to recolor just that icon for white-on-red here -
// no second, custom close control needed.
const criticalDialogClose = `
  .JS-dialog-close {
    color: #fff;
  }
  .JS-dialog-close:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`
const CriticalConfirmDialog = styled(ConfirmDialog)`${criticalDialogClose}`
const CriticalDialog = styled(Dialog)`${criticalDialogClose}`

const CaseInfoBox = styled.div`
  background: #f2f2f2;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  padding: 0.75rem 1rem;
  font-size: 13px;
  color: #3c3c3c;
  margin-bottom: 1.5rem;

  > div {
    display: flex;
    gap: 0.25rem;

    & + div {
      margin-top: 0.25rem;
    }
  }
`

const CharactersCount = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #b3b3b3;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`

const DeleteList = styled.ul`
  list-style: disc;
  padding-left: 1.25rem;
  margin-bottom: 1rem;

  li {
    list-style: disc;
  }
`

const WarningList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
`

const WarningItem = styled.li`
  font-size: 12px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ffe0b2;
  border-radius: 4px;
  background: #fff8f0;

  & + & {
    margin-top: 0.375rem;
  }
`

const WarningPhase = styled.span`
  font-weight: 600;
  color: #c62828;
  margin-right: 0.375rem;
`

const WarningTarget = styled.code`
  display: block;
  font-size: 11px;
  color: #888;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 0.125rem;
`

const DeletingMessage = styled.p`
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 1.5rem 0;
`

const WARNING_PHASE_LABELS = {
  EVE: 'Eve documents',
  STORAGE: 'Storage',
  PACS: 'DICOM / PACS',
  MPI: 'Patient index (MPI)',
  LOCAL_FILES: 'Local files',
}

const getPatientName = (caseRequest) => [
  caseRequest?.patientInfo?.firstName,
  caseRequest?.patientInfo?.middleName,
  caseRequest?.patientInfo?.lastName
].filter(Boolean).join(' ')

const DeleteDialogTitle = () => <DeleteDialogHeader>
  {/* <DeleteDialogIcon/> */}
  <Icon icon="folder-breaking" css="font-size: 40px; color: #fff; flex-shrink: 0; margin-right: 0.5rem;"/>
  <DeleteDialogTitleText>
    <strong>Delete case?</strong>
    <span>This CANNOT be undone.</span>
  </DeleteDialogTitleText>
</DeleteDialogHeader>

const DeletedDialogTitle = () => <DeleteDialogHeader>
  <Icon icon="folder-breaking" css="font-size: 40px; color: #fff; flex-shrink: 0; margin-right: 0.5rem;"/>
  <DeleteDialogTitleText>
    <strong>Case deleted</strong>
    <span>Some items could not be fully removed.</span>
  </DeleteDialogTitleText>
</DeleteDialogHeader>

const CaseInfo = ({caseRequest}) => <CaseInfoBox>
  <div><span>Case Id:</span><strong>&nbsp;{caseRequest.requestCode}</strong></div>
  <div><span>Patient:</span><strong className="fs-exclude">&nbsp;{getPatientName(caseRequest)}</strong></div>
</CaseInfoBox>

export const useDeleteCaseDialog = (caseRequest, onSuccess) => {

  const [step, setStep] = useState(1)
  const [deleteResult, setDeleteResult] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const toggle = useDialogWithToggle((toggle) => (
    step === 1
      ? <CriticalConfirmDialog
          closeDialog={toggle.close}
          title={<DeleteDialogTitle/>}
          action={() => Promise.resolve()}
          onSuccess={() => setStep(2)}
          keepOnSuccess
          confirm={<Button alert icon="delete">Yes, I am sure</Button>}
        >
          <CaseInfo caseRequest={caseRequest}/>
          <p>Everyone will lose access to all data of this case, which includes:</p>
          <DeleteList>
            <li>Patient information</li>
            <li>Case notes</li>
            <li>Signed agreements</li>
            <li>Medical records</li>
            <li>Reports</li>
          </DeleteList>
          <p>Are you sure you want to delete the case?</p>
        </CriticalConfirmDialog>
      : step === 2
        ? <CriticalConfirmDialog
            closeDialog={isDeleting ? () => {} : toggle.close}
            title={<DeleteDialogTitle/>}
            values={{reason: ""}}
            preValidation={preValidation}
            confirmChanges={false}
            action={(values) => {
              setIsDeleting(true)
              return deleteCase(
                caseRequest.requestId || caseRequest.id,
                values.reason.trim()
              ).finally(() => setIsDeleting(false))
            }}
            onSuccess={(_formValues, response) => {
              if (response?.warnings?.length > 0) {
                setDeleteResult(response)
                setStep(3)
              } else {
                toggle.close()
                setNotification("Case deleted successfully.")
                onSuccess(response)
              }
            }}
            keepOnSuccess
            closeButton={<Button disabled={isDeleting}>Cancel</Button>}
            confirm={<FormValue path="reason">{reason =>
              <FormValue path="deleteConfirmation">{deleteConfirmation =>
                <Button
                  alert
                  icon="delete"
                  type="submit"
                  disabled={isDeleting || (reason || '').length < MIN_REASON_LENGTH || deleteConfirmation !== DELETE_CONFIRMATION}
                >Delete case</Button>
              }</FormValue>
            }</FormValue>}
          >{form => <>
            <CaseInfo caseRequest={caseRequest}/>
            {form.isLoading
              ? <DeletingMessage>Deleting case, please wait…</DeletingMessage>
              : <>
                  <p>Please type in the field below why you want to delete this case.</p>
                  <TextInput
                    path="reason"
                    textarea
                    autoResize
                    mandatory
                    placeholder="Type the reason to delete this case here"
                  />
                  <FormValue path="reason">{reason => <CharactersCount>
                    <span>Min characters: {MIN_REASON_LENGTH}</span>
                    <span>{(reason || "").length} / {MAX_REASON_LENGTH}</span>
                  </CharactersCount>}</FormValue>
                  <p>To confirm deletion, please type delete below:</p>
                  <TextInput
                    path="deleteConfirmation"
                    mandatory
                    placeholder="delete"
                    css="margin-bottom: 1rem;"
                  />
                </>
            }
          </>}</CriticalConfirmDialog>
        : (() => {
            const handleClose = () => {
              toggle.close()
              setNotification("Case deleted.")
              onSuccess(deleteResult)
            }
            return <CriticalDialog
              closeDialog={handleClose}
              title={<DeletedDialogTitle/>}
              footer={<Button onClick={handleClose}>Close</Button>}
            >
              <p>
                Case <strong>{deleteResult?.requestCode}</strong> has been deleted
                {deleteResult?.recordsDeleted > 0 && `, ${deleteResult.recordsDeleted} record(s) removed`}.
              </p>
              <p>The following items could not be fully cleaned up and may require manual follow-up. Full details are available in the audit log.</p>
              <WarningList>
                {(deleteResult?.warnings || []).map((w, i) => (
                  <WarningItem key={i}>
                    <WarningPhase>{WARNING_PHASE_LABELS[w.phase] || w.phase}</WarningPhase>
                    {w.message}
                    {w.target && <WarningTarget title={w.target}>{w.target}</WarningTarget>}
                  </WarningItem>
                ))}
              </WarningList>
            </CriticalDialog>
          })()
  ), [step, caseRequest, deleteResult, onSuccess, isDeleting])

  useEffect(() => {
    if (toggle.isOpen) {
      setStep(1)
      setDeleteResult(null)
      setIsDeleting(false)
    }
  }, [toggle.isOpen])

  return toggle.open
}
